/**
 * Owner service — lets a signed-in business owner manage their own listings.
 *
 * Backend endpoints (all require a Bearer token):
 *   GET    /businesses/mine      every listing owned by the user
 *   PATCH  /businesses/:id       update an owned listing
 *   DELETE /businesses/:id       withdraw an owned (non-live) listing
 *
 * Demo data is only used when no API URL is configured (preview mode); with a
 * live API, failures propagate so the portal can show an error + retry state.
 */

import { apiGetPagedAuth, apiGetAuth, apiPatchAuth, apiDeleteAuth, isApiConfigured } from "./http";
import { demoOwnerListings } from "@/mocks/ownerListings";
import { demoOwnerStats } from "@/mocks/ownerStats";

// ── Types ─────────────────────────────────────────────────────────────────────

export type OwnerListingStatus = "pending" | "active" | "rejected" | "suspended";

export interface OwnerListing {
  id:               number;
  slug:             string;
  name:             string;
  tagline:          string | null;
  description:      string | null;
  address:          string | null;
  phone:            string | null;
  whatsapp:         string | null;
  email:            string | null;
  website:          string | null;
  videoUrl?:        string | null;
  coverUrl?:        string | null;
  logoUrl?:         string | null;
  gallery?:         string[];
  status:           OwnerListingStatus;
  rejectionReason:  string | null;
  rating:           number;
  reviewCount:      number;
  isVerified:       number | boolean;
  isOpenNow:        number | boolean;
  createdAt:        string;
  updatedAt:        string;
  categoryId:       number;
  categoryName:     string;
  categorySlug:     string;
  districtId:       number;
  districtName:     string;
  districtSlug:     string;
  townId:           number | null;
  townName:         string | null;
  planSlug:         string;
}

export interface ListingUpdatePayload {
  name?:        string;
  tagline?:     string;
  description?: string;
  address?:     string;
  phone?:       string;
  whatsapp?:    string;
  email?:       string;
  website?:     string;
  videoUrl?:    string;
  coverUrl?:    string;
  logoUrl?:     string;
  gallery?:     string[];
}

const delay = (ms = 320): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

// ── Public API ────────────────────────────────────────────────────────────────

/** Fetch every listing owned by the signed-in user. */
export async function getMyListings(token: string | null): Promise<OwnerListing[]> {
  if (isApiConfigured() && token) {
    const result = await apiGetPagedAuth<OwnerListing>("/businesses/mine?limit=50", token);
    return result.data;
  }
  await delay();
  return demoOwnerListings as OwnerListing[];
}

/** Update an owned listing. Rejected listings are re-queued for review. */
export async function updateMyListing(
  token: string | null,
  id: number,
  payload: ListingUpdatePayload,
): Promise<void> {
  if (isApiConfigured() && token) {
    await apiPatchAuth<{ id: number }>(`/businesses/${id}`, payload, token);
    return;
  }
  await delay(500);
}

/** Withdraw an owned listing that is not yet live. */
export async function deleteMyListing(token: string | null, id: number): Promise<void> {
  if (isApiConfigured() && token) {
    await apiDeleteAuth<{ id: number }>(`/businesses/${id}`, token);
    return;
  }
  await delay(360);
}

// ── Owner listing performance ─────────────────────────────────────────────────

export interface OwnerListingStat {
  id:              number;
  name:            string;
  slug:            string;
  status:          OwnerListingStatus;
  views:           number;
  rating:          number;
  reviews:         number;
  leads:           number;
  unreadLeads:     number;
  approvedReviews: number;
}

export interface OwnerStats {
  listings: OwnerListingStat[];
  totals:   { views: number; leads: number; reviews: number };
}

/** Per-listing views, enquiries and review counts for the signed-in owner. */
export async function getOwnerStats(token: string | null): Promise<OwnerStats> {
  if (isApiConfigured() && token) {
    return apiGetAuth<OwnerStats>("/businesses/mine/stats", token);
  }
  await delay();
  return demoOwnerStats as OwnerStats;
}